/**
 * FundingEntitiesEditor — the correspondent `fundingEntities[]` editor
 * (correspondent-admin-and-locks.md §5a).
 *
 * The closing / funding entities a correspondent originates and closes loans in
 * — the lender / mortgagee on the note and recorded mortgage. The computed
 * `mortgageeClause` is what acquisitions `validate_mortgagee` checks a loan's
 * populated mortgagee against, so these have to be right.
 *
 * Shape: a collapsible card per entity. Collapsed it's a one-line summary (name,
 * address, ISAOA/ATIMA + active pills); open it's a 12-column form capped at a
 * readable measure with a settings rail on the right. The cap is the point —
 * full-width text inputs across a 1400px panel are unreadable and make a
 * two-character State field look like a paragraph field.
 *
 * State is a select over the shared `selectOptionsUsaStates` (value = the
 * two-letter code) — same control every address field in the app uses.
 */
import { useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";

import AddRounded from "@mui/icons-material/AddRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";

import Flx from "../../../components/layout/Flx";
import { selectOptionsUsaStates } from "../../../constants/selectOptions/selectOptionsUsaStates";
import { teal, status as statusColors } from "../../../theme/colors";
import { T } from "../../SupportScreen/components/supportUi";

// Mirrors `CorrespondentFundingEntity.mortgageeClause` in the backend model
// (app/models/correspondents.py) EXACTLY — same line order, same
// `"city, state zip".strip(' ,')` behaviour — so the preview shown while editing
// matches the value the server computes on save byte for byte. If that computed
// field changes, change this with it.
export const buildMortgageeClause = (e) => {
  if (!e?.name) return null;
  const lines = [e.isaoaAtima !== false ? `${e.name} ISAOA/ATIMA` : e.name];
  if (e.addressLine1) lines.push(e.addressLine1);
  if (e.addressLine2) lines.push(e.addressLine2);
  const loc = `${e.city || ""}, ${e.state || ""} ${e.zipCode || ""}`
    .trim()
    .replace(/^[\s,]+|[\s,]+$/g, "");
  if (loc) lines.push(loc);
  return lines.join("\n");
};

// One-line address for the collapsed summary.
const summaryAddress = (e) => {
  const street = [e.addressLine1, e.addressLine2].filter(Boolean).join(", ");
  const loc = `${e.city || ""}${e.city && e.state ? ", " : ""}${e.state || ""} ${
    e.zipCode || ""
  }`.trim();
  return [street, loc].filter(Boolean).join(" · ");
};

const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "6px",
    fontSize: "13px",
    background: "#fff",
  },
};

function FieldLabel({ children, optional }) {
  return (
    <Box
      sx={{
        fontSize: "11px",
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: T.ink300,
        mb: 0.625,
      }}
    >
      {children}
      {optional && (
        <Box
          component="span"
          sx={{
            color: "#b3bcc7",
            fontWeight: 500,
            textTransform: "none",
            letterSpacing: 0,
          }}
        >
          {" "}
          · optional
        </Box>
      )}
    </Box>
  );
}

function Pill({ tone, children }) {
  const TONES = {
    teal: { bg: teal[50], fg: teal[700] },
    violet: { bg: "#f2eefb", fg: "#6b4fbb" },
    slate: { bg: "#eef1f5", fg: T.ink500 },
  };
  const t = TONES[tone] || TONES.slate;
  return (
    <Box
      component="span"
      sx={{
        flexShrink: 0,
        fontSize: "11px",
        fontWeight: 600,
        px: "9px",
        py: "2px",
        borderRadius: "999px",
        background: t.bg,
        color: t.fg,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  );
}

/** A 12-column grid cell. */
function Cell({ span = 12, spanSm, children }) {
  return (
    <Box
      sx={{
        minWidth: 0,
        gridColumn: {
          xs: "span 12",
          sm: `span ${spanSm ?? span}`,
          md: `span ${span}`,
        },
      }}
    >
      {children}
    </Box>
  );
}

function EntityCard({ entity, index, onChange, onRemove, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const set = (k, v) => onChange(index, k, v);
  // Prefer the live mirror while editing; fall back to whatever the server last
  // computed (covers a row whose name was cleared).
  const clause = buildMortgageeClause(entity) || entity.mortgageeClause;

  return (
    <Box
      sx={{
        border: `1px solid ${open ? teal[200] : T.line}`,
        borderRadius: "8px",
        boxShadow: open ? `0 0 0 3px ${teal[50]}` : "none",
        transition: "border-color .15s, box-shadow .15s",
        "&:hover": { borderColor: open ? teal[200] : T.ink300 },
      }}
    >
      {/* ── summary ─────────────────────────────────────────────────────── */}
      <Flx
        ac
        g={1.5}
        onClick={() => setOpen((o) => !o)}
        sx={{ px: 1.75, py: 1.5, cursor: "pointer", userSelect: "none" }}
      >
        <ChevronRightRounded
          sx={{
            fontSize: 18,
            color: T.ink300,
            flexShrink: 0,
            transform: open ? "rotate(90deg)" : "none",
            transition: "transform .2s",
          }}
        />
        <Flx
          ac
          wrap
          g={1.25}
          sx={{ flex: 1, minWidth: 0, alignItems: "baseline" }}
        >
          <Box
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: T.ink900,
              whiteSpace: "nowrap",
            }}
          >
            {entity.name || "New funding entity"}
          </Box>
          <Box
            sx={{
              fontSize: "12.5px",
              color: T.ink500,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: { xs: "none", sm: "block" },
            }}
          >
            {summaryAddress(entity)}
          </Box>
        </Flx>
        {entity.isaoaAtima !== false && <Pill tone="violet">ISAOA/ATIMA</Pill>}
        <Pill tone={entity.active !== false ? "teal" : "slate"}>
          {entity.active !== false ? "Active" : "Inactive"}
        </Pill>
        <Tooltip title="Remove entity">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(index);
            }}
            sx={{
              flexShrink: 0,
              color: T.ink300,
              "&:hover": {
                color: statusColors.error.fg,
                background: statusColors.error.bg,
              },
            }}
          >
            <DeleteOutlineRounded sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      </Flx>

      {/* ── body ────────────────────────────────────────────────────────── */}
      {open && (
        <Box sx={{ borderTop: `1px solid ${T.line}`, px: 2, pt: 1.75, pb: 2 }}>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 2, md: 3.5 },
              flexDirection: { xs: "column", md: "row" },
              alignItems: "stretch",
            }}
          >
            {/* form — capped so the inputs keep a readable measure instead of
                stretching the full width of the panel */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                maxWidth: 900,
                display: "grid",
                gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
                columnGap: 2,
                rowGap: 1.75,
              }}
            >
              <Cell span={8}>
                <FieldLabel>Entity name</FieldLabel>
                <TextField
                  size="small"
                  fullWidth
                  value={entity.name || ""}
                  onChange={(e) => set("name", e.target.value)}
                  sx={FIELD_SX}
                />
              </Cell>
              <Cell span={4}>
                <FieldLabel optional>Short name</FieldLabel>
                <TextField
                  size="small"
                  fullWidth
                  value={entity.shortName || ""}
                  onChange={(e) => set("shortName", e.target.value)}
                  placeholder="e.g. MF05"
                  sx={FIELD_SX}
                />
              </Cell>

              <Cell span={8}>
                <FieldLabel>Address line 1</FieldLabel>
                <TextField
                  size="small"
                  fullWidth
                  value={entity.addressLine1 || ""}
                  onChange={(e) => set("addressLine1", e.target.value)}
                  sx={FIELD_SX}
                />
              </Cell>
              <Cell span={4}>
                <FieldLabel optional>Address line 2</FieldLabel>
                <TextField
                  size="small"
                  fullWidth
                  value={entity.addressLine2 || ""}
                  onChange={(e) => set("addressLine2", e.target.value)}
                  placeholder="Suite, floor…"
                  sx={FIELD_SX}
                />
              </Cell>

              <Cell span={5} spanSm={6}>
                <FieldLabel>City</FieldLabel>
                <TextField
                  size="small"
                  fullWidth
                  value={entity.city || ""}
                  onChange={(e) => set("city", e.target.value)}
                  sx={FIELD_SX}
                />
              </Cell>
              <Cell span={3} spanSm={6}>
                <FieldLabel>State</FieldLabel>
                {/* Initials only — both in the closed control and in the menu.
                    The option list is the shared `selectOptionsUsaStates` (so
                    there's one source of truth for the 50 + DC codes) but only
                    its `value` is rendered; "FL - Florida" would truncate in a
                    3-of-12 column and this field is only ever a code. */}
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={entity.state || ""}
                  onChange={(e) => set("state", e.target.value)}
                  sx={FIELD_SX}
                  SelectProps={{
                    MenuProps: { PaperProps: { sx: { maxHeight: 320 } } },
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: 13, color: T.ink300 }}>
                    —
                  </MenuItem>
                  {selectOptionsUsaStates.map((o) => (
                    <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>
                      {o.value}
                    </MenuItem>
                  ))}
                </TextField>
              </Cell>
              <Cell span={4} spanSm={12}>
                <FieldLabel>ZIP code</FieldLabel>
                <TextField
                  size="small"
                  fullWidth
                  value={entity.zipCode || ""}
                  onChange={(e) => set("zipCode", e.target.value)}
                  inputProps={{ maxLength: 10 }}
                  sx={FIELD_SX}
                />
              </Cell>

              <Cell span={12}>
                <FieldLabel optional>Notes</FieldLabel>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  value={entity.notes || ""}
                  onChange={(e) => set("notes", e.target.value)}
                  sx={FIELD_SX}
                />
              </Cell>

            </Box>

            {/* ── mortgagee clause column ──────────────────────────────────
                Its own column between the form and the settings, because it's
                OUTPUT, not input: a read-only value the backend computes from
                the fields on the left. Sitting beside them makes the
                cause-and-effect visible while you type. */}
            <Flx
              column
              g={1}
              sx={{
                width: { xs: "auto", md: 300 },
                flexShrink: 0,
                minWidth: 0,
                borderLeft: { xs: "none", md: `1px solid ${T.line}` },
                borderTop: { xs: `1px solid ${T.line}`, md: "none" },
                pl: { xs: 0, md: 2.5 },
                pt: { xs: 1.75, md: 0 },
              }}
            >
              <Box
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: T.ink300,
                }}
              >
                Mortgagee clause
              </Box>
              {clause ? (
                <Box
                  sx={{
                    background: teal[50],
                    border: `1px solid ${teal[200]}`,
                    borderRadius: "6px",
                    px: 1.5,
                    py: 1.25,
                    fontSize: "12.5px",
                    lineHeight: 1.6,
                    color: teal[800],
                    whiteSpace: "pre-line",
                    overflowWrap: "anywhere",
                    fontFamily:
                      "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                  }}
                >
                  {clause}
                </Box>
              ) : (
                <Box sx={{ fontSize: "12.5px", color: T.ink300 }}>
                  Enter an entity name to generate the clause.
                </Box>
              )}
              <Box
                sx={{ fontSize: "11.5px", color: T.ink300, lineHeight: 1.55 }}
              >
                Read-only. Built by the backend from the name, the ISAOA/ATIMA
                toggle and the address, then re-computed on every save — a
                loan&apos;s mortgagee is validated against this exact string.
              </Box>
            </Flx>

            {/* settings rail */}
            <Flx
              column
              g={2}
              sx={{
                width: { xs: "auto", md: 150 },
                flexShrink: 0,
                borderLeft: { xs: "none", md: `1px solid ${T.line}` },
                borderTop: { xs: `1px solid ${T.line}`, md: "none" },
                pl: { xs: 0, md: 2.5 },
                pt: { xs: 1.75, md: 0 },
                flexDirection: { xs: "row", md: "column" },
                flexWrap: { xs: "wrap", md: "nowrap" },
                alignItems: { xs: "center", md: "stretch" },
              }}
            >
              <Box
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: T.ink300,
                  display: { xs: "none", md: "block" },
                }}
              >
                Settings
              </Box>

              <Flx ac g={1}>
                <Switch
                  size="small"
                  checked={entity.isaoaAtima !== false}
                  onChange={(e) => set("isaoaAtima", e.target.checked)}
                />
                <Box sx={{ fontSize: "13px", color: T.ink700 }}>
                  ISAOA/ATIMA
                </Box>
              </Flx>
              <Flx ac g={1}>
                <Switch
                  size="small"
                  checked={entity.active !== false}
                  onChange={(e) => set("active", e.target.checked)}
                />
                <Box sx={{ fontSize: "13px", color: T.ink700 }}>Active</Box>
              </Flx>

              <Box sx={{ flex: { xs: "unset", md: 1 } }} />
              <Button
                size="small"
                variant="text"
                startIcon={<DeleteOutlineRounded sx={{ fontSize: 15 }} />}
                onClick={() => onRemove(index)}
                sx={{
                  alignSelf: { xs: "center", md: "flex-start" },
                  color: T.ink300,
                  fontSize: "13px",
                  px: 1,
                  "&:hover": {
                    color: statusColors.error.fg,
                    background: statusColors.error.bg,
                  },
                }}
              >
                Remove entity
              </Button>
            </Flx>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default function FundingEntitiesEditor({
  entities = [],
  onAdd,
  onChange,
  onRemove,
}) {
  return (
    <Box
      component="section"
      sx={{
        border: `1px solid ${T.line}`,
        borderRadius: "12px",
        background: "#fff",
        boxShadow: T.shadow,
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <Flx
        ac
        g={1}
        sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${T.line}` }}
      >
        <Box
          sx={{
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: T.ink500,
          }}
        >
          Funding entities ({entities.length})
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="text"
          startIcon={<AddRounded sx={{ fontSize: 17 }} />}
          onClick={onAdd}
        >
          Add funding entity
        </Button>
      </Flx>

      <Box sx={{ px: 2.5, pt: 1.75, fontSize: "13px", color: T.ink500 }}>
        The closing / funding entities this correspondent originates and closes
        loans in. The mortgagee clause is computed from the name, the ISAOA/ATIMA
        flag and the address — it&apos;s what a loan&apos;s populated mortgagee
        is validated against, so keep these accurate.
      </Box>

      <Flx column g={1.5} sx={{ px: 2.5, py: 2 }}>
        {entities.length === 0 ? (
          <Box sx={{ fontSize: "13px", color: T.ink300 }}>
            No funding entities yet.
          </Box>
        ) : (
          entities.map((e, i) => (
            <EntityCard
              // Index-keyed on purpose: rows have no stable id until the server
              // assigns `oid` on first save, and keying on a mutating field
              // would remount the card mid-edit and drop focus.
              key={e.oid || `new-${i}`}
              entity={e}
              index={i}
              onChange={onChange}
              onRemove={onRemove}
              // A freshly added row opens so it can be filled in immediately,
              // and a lone entity opens too — collapsing the only thing on the
              // tab just hides it behind an extra click.
              defaultOpen={!e.oid || entities.length === 1}
            />
          ))
        )}
      </Flx>
    </Box>
  );
}
